import {useEffect, useMemo, useRef, useState} from 'react';
import {observer} from 'mobx-react';
import {useQuery, useQueryClient} from 'react-query';
import {useNavigate, useParams} from 'react-router-dom';
import {UserAPIRequestImpl} from '../../../../../../api/user-api/user-api-request';
import {Avatar} from '../../../../../../ui/atoms/avatar';
import {Checkbox} from '../../../../../../ui/atoms/checkbox';
import {MagnifyingGlassIcon} from '../../../../../../ui/atoms/icons';
import {HorizontalDots} from '../../../../../../ui/atoms/icons/horizontal-dots';
import {LockIcon} from '../../../../../../ui/atoms/icons/lock';
import {Tooltip} from '../../../../../../ui/molecules/tooltip';
import {activateNotification} from '../../../../../../ui/molecules/notification/activate-notification';
import {ItemLoaderLine} from '../../../../../../ui/organisms/item-loader-line';
import {useStoreContext} from '../../../../../../store/store-context';
import {parseErrorContent} from '../../../../../../utils/error-handling';
import {setFirebaseData} from '../../../../../../utils/firebase-handler';
import {generateRandomDigits} from '../../../../../../utils/generate-random-numbers';
import {
  isProtectedGroup,
  normalizeGroupMembers,
  TeamsAndGroupsGroup,
} from '../admin-group-type/admin-group-type-page';
import {GroupEditSidecar} from '../admin-group-type/group-edit-sidecar';
import {
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
  GroupMore,
  GroupPill,
  ManagerCell,
  MemberAction,
  MemberActionButton,
  MemberEmpty,
  MemberCell,
  MemberGroupsCell,
  MemberIdentity,
  MemberIdentityText,
  MemberMenu,
  MemberMenuButton,
  MemberMenuHint,
  MemberMeta,
  MemberName,
  MemberNameLine,
  MemberPageButton,
  MemberPagination,
  MemberPrimaryButton,
  MemberRoleBadge,
  MemberSearchIcon,
  MemberSearchInput,
  MemberTableHeader,
  MemberTableRow,
  SecondaryButton,
} from '../admin-member/member-redesign.styles';
import {reviewerId} from '../admin-member/member-role';
import {formatTimezoneForDisplay} from '../admin-member/admin-member-components/active-member/active.utils';
import {groupRequestImpl} from './admin-group-request';
import {
  Breadcrumbs,
  CandidateCopy,
  CandidateFade,
  CandidateList,
  CandidateMeta,
  CandidateName,
  CandidateNameLine,
  CandidateRow,
  CandidateViewport,
  ConfirmationText,
  DetailPage,
  EditButton,
  EmptyState,
  ErrorCopy,
  GroupTitle,
  GroupMemberCard,
  LeadCopy,
  LeadIdentity,
  LeadLabel,
  LeadMeta,
  LeadName,
  LeadSection,
  MemberCount,
  InlineMemberSearchBar,
  InlineMemberSearchField,
  ModalBody,
  ModalShell,
  PrimaryButton,
  ReadOnlyPill,
  SearchWrap,
  SelectedCount,
  SummaryCard,
  SummaryHeader,
  SummaryHeading,
  TitleLine,
  TypePill,
} from './group-detail.styles';

const MEMBERS_PER_PAGE = 10;

const normalizeList = (response: any): any[] => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.results)) return response.results;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const getId = (value: any) =>
  typeof value === 'object' ? value?.id || '' : value || '';

export const excludeRemovedGroupMembers = (
  members: any[],
  removedMemberIds: ReadonlySet<string>,
) => members.filter((member) => !removedMemberIds.has(getId(member)));

const displayName = (user: any) => {
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
  return name || user?.email || 'Unnamed member';
};

const groupTypeName = (group: any, groupTypes: any[]) => {
  if (typeof group?.groupType === 'object' && group.groupType?.name) {
    return group.groupType.name.replace(/s$/i, '');
  }

  const type = groupTypes.find(
    (candidate) => candidate.id === getId(group?.groupType),
  );
  return (type?.name || 'Uncategorised').replace(/s$/i, '');
};

const membershipGroupId = (membership: any) =>
  getId(membership?.group || membership?.groupId);

const memberMatchesSearch = (member: any, search: string) =>
  [displayName(member), member?.email, member?.jobTitle, member?.timezone]
    .filter(Boolean)
    .some((value) =>
      String(value).toLowerCase().includes(search.trim().toLowerCase()),
    );

export const groupDetailCandidates = (users: any[], memberIds: Set<string>) =>
  users.filter(
    (user) =>
      user?.id &&
      !memberIds.has(user.id) &&
      user.status !== 'invitation_sent' &&
      !user.deletedAt,
  );

export const countLeadReportsInGroup = (members: any[], leadId: string) =>
  leadId
    ? members.filter((member) => reviewerId(member?.reviewer) === leadId).length
    : 0;

type AddMembersDialogProps = {
  groupName: string;
  candidates: any[];
  loading: boolean;
  onClose: () => void;
  onAdd: (userIds: string[]) => void;
};

const AddMembersDialog = ({
  groupName,
  candidates,
  loading,
  onClose,
  onAdd,
}: AddMembersDialogProps) => {
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [scrollState, setScrollState] = useState({
    canScrollUp: false,
    canScrollDown: false,
  });
  const listRef = useRef<HTMLDivElement>(null);
  const filteredCandidates = candidates.filter((candidate) =>
    memberMatchesSearch(candidate, search),
  );

  const updateScrollState = () => {
    const list = listRef.current;
    if (!list) return;
    setScrollState({
      canScrollUp: list.scrollTop > 2,
      canScrollDown: list.scrollTop + list.clientHeight < list.scrollHeight - 2,
    });
  };

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !loading) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [loading, onClose]);

  useEffect(() => {
    listRef.current?.scrollTo({top: 0});
    const frame = window.requestAnimationFrame(updateScrollState);
    return () => window.cancelAnimationFrame(frame);
  }, [filteredCandidates.length]);

  const toggleCandidate = (id: string) => {
    if (loading) return;
    setSelectedIds((current) =>
      current.includes(id)
        ? current.filter((candidateId) => candidateId !== id)
        : [...current, id],
    );
  };

  return (
    <DialogOverlay role="presentation">
      <ModalShell
        role="dialog"
        aria-modal="true"
        aria-label={`Add to ${groupName}`}>
        <DialogHeader>
          <DialogTitle>Add to {groupName}</DialogTitle>
          <SecondaryButton type="button" disabled={loading} onClick={onClose}>
            Close ×
          </SecondaryButton>
        </DialogHeader>
        <ModalBody>
          <SearchWrap>
            <input
              autoFocus
              aria-label="Search members"
              placeholder="Search members"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <MagnifyingGlassIcon />
          </SearchWrap>
          <CandidateViewport>
            {scrollState.canScrollUp && (
              <CandidateFade $position="top" aria-hidden="true" />
            )}
            <CandidateList ref={listRef} onScroll={updateScrollState}>
              {filteredCandidates.map((candidate) => {
                const selected = selectedIds.includes(candidate.id);
                return (
                  <CandidateRow
                    key={candidate.id}
                    $selected={selected}
                    onClick={() => toggleCandidate(candidate.id)}>
                    <Checkbox
                      checked={selected}
                      boxSize="small"
                      onChange={() => toggleCandidate(candidate.id)}
                      onClick={(event) => event.stopPropagation()}
                    />
                    <Avatar
                      name={displayName(candidate)}
                      userId={candidate.id}
                      src={candidate.avatar?.url || ''}
                      size="md"
                      tooltip={false}
                    />
                    <CandidateCopy>
                      <CandidateNameLine>
                        <CandidateName>{displayName(candidate)}</CandidateName>
                        {candidate.role === 'admin' && (
                          <MemberRoleBadge>Admin</MemberRoleBadge>
                        )}
                      </CandidateNameLine>
                      <CandidateMeta>
                        {candidate.jobTitle || candidate.email}
                      </CandidateMeta>
                    </CandidateCopy>
                  </CandidateRow>
                );
              })}
              {!filteredCandidates.length && (
                <MemberEmpty>
                  {search
                    ? 'No members match your search.'
                    : 'Everyone is already in this group.'}
                </MemberEmpty>
              )}
            </CandidateList>
            {scrollState.canScrollDown && (
              <CandidateFade $position="bottom" aria-hidden="true" />
            )}
          </CandidateViewport>
        </ModalBody>
        <DialogFooter>
          <SelectedCount>{selectedIds.length} selected</SelectedCount>
          <SecondaryButton type="button" disabled={loading} onClick={onClose}>
            Close
          </SecondaryButton>
          <PrimaryButton
            type="button"
            disabled={!selectedIds.length || loading}
            onClick={() => onAdd(selectedIds)}>
            {loading ? 'Adding…' : 'Add to group'}
          </PrimaryButton>
        </DialogFooter>
      </ModalShell>
    </DialogOverlay>
  );
};

export const GroupDetailPage = observer(() => {
  const {groupId = ''} = useParams() as {groupId?: string};
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    groupTypeStore: {groupType},
  } = useStoreContext();
  const request = useMemo(() => new groupRequestImpl(), []);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [menuMemberId, setMenuMemberId] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [removingMember, setRemovingMember] = useState<any>(null);
  const [removing, setRemoving] = useState(false);
  const [locallyRemovedMemberIds, setLocallyRemovedMemberIds] = useState<
    Set<string>
  >(new Set());

  const groupsQuery = useQuery(['teams-and-groups-landing'], () =>
    request.fetchGroups(),
  );
  const usersQuery = useQuery(['activeUsers', 'all'], () =>
    new UserAPIRequestImpl().fetchAllUsers(),
  );
  const membershipsQuery = useQuery(['initGroupMembers'], () =>
    request.fetchGroupsMembers(),
  );
  const membersQuery = useQuery(
    ['group-detail-members', groupId],
    () => request.fetchGroupMembers(groupId),
    {enabled: Boolean(groupId)},
  );

  const groups = useMemo(() => normalizeList(groupsQuery.data), [
    groupsQuery.data,
  ]);
  const rawUsers = useMemo(() => normalizeList(usersQuery.data), [
    usersQuery.data,
  ]);
  const memberships = useMemo(() => normalizeList(membershipsQuery.data), [
    membershipsQuery.data,
  ]);
  const group = groups.find((candidate) => candidate.id === groupId);

  const users = useMemo(() => {
    const membershipByUser = new Map<string, any[]>();
    memberships.forEach((membership) => {
      const userId = getId(membership?.user);
      if (!userId) return;
      membershipByUser.set(userId, [
        ...(membershipByUser.get(userId) || []),
        membership,
      ]);
    });

    return rawUsers.map((user) => {
      const userMemberships = membershipByUser.get(user.id) || [];
      const populatedGroups = userMemberships
        .map((membership) =>
          groups.find(
            (candidate) => candidate.id === membershipGroupId(membership),
          ),
        )
        .filter(Boolean);
      return {
        ...user,
        groups: userMemberships,
        group: populatedGroups,
      };
    });
  }, [groups, memberships, rawUsers]);

  const fetchedMembers = useMemo(() => {
    const normalized = normalizeGroupMembers(membersQuery.data, users);
    const unique = new Map<string, any>();
    normalized.forEach((member) => {
      const id = getId(member);
      if (!id) return;
      unique.set(id, users.find((user) => user.id === id) || member);
    });
    return Array.from(unique.values());
  }, [membersQuery.data, users]);
  const members = useMemo(
    () => excludeRemovedGroupMembers(fetchedMembers, locallyRemovedMemberIds),
    [fetchedMembers, locallyRemovedMemberIds],
  );

  const leadId = getId(group?.groupAdmin);
  const lead =
    (typeof group?.groupAdmin === 'object' && group.groupAdmin) ||
    users.find((user) => user.id === leadId);
  const leadReportCount = countLeadReportsInGroup(members, leadId);
  const protectedGroup = group ? isProtectedGroup(group) : false;
  const memberIds = useMemo(() => new Set(members.map((member) => member.id)), [
    members,
  ]);
  const candidates = useMemo(() => groupDetailCandidates(users, memberIds), [
    memberIds,
    users,
  ]);
  const visibleMembers = members.filter((member) =>
    memberMatchesSearch(member, search),
  );
  const totalPages = Math.max(
    1,
    Math.ceil(visibleMembers.length / MEMBERS_PER_PAGE),
  );
  const pagedMembers = visibleMembers.slice(
    (page - 1) * MEMBERS_PER_PAGE,
    page * MEMBERS_PER_PAGE,
  );
  const loading =
    groupsQuery.isLoading || usersQuery.isLoading || membersQuery.isLoading;

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    setPage(1);
  }, [search]);

  useEffect(() => {
    setLocallyRemovedMemberIds(new Set());
  }, [groupId]);

  useEffect(() => {
    const fetchedMemberIds = new Set(
      fetchedMembers.map((member) => getId(member)).filter(Boolean),
    );

    setLocallyRemovedMemberIds((current) => {
      const next = new Set(
        Array.from(current).filter((memberId) =>
          fetchedMemberIds.has(memberId),
        ),
      );
      return next.size === current.size ? current : next;
    });
  }, [fetchedMembers]);

  useEffect(() => {
    const closeMenu = () => setMenuMemberId('');
    document.addEventListener('click', closeMenu);
    return () => document.removeEventListener('click', closeMenu);
  }, []);

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries(['group-detail-members', groupId]),
      queryClient.invalidateQueries(['initGroupMembers']),
      queryClient.invalidateQueries(['teams-and-groups-landing']),
      queryClient.invalidateQueries(['teams-and-groups-member-previews']),
    ]);
    setFirebaseData('updates/group', generateRandomDigits(16));
  };

  const addMembers = async (userIds: string[]) => {
    if (!group || adding || protectedGroup) return;
    setAdding(true);
    try {
      await request.addGroupMember(
        {add: {users: userIds, type: 'add'}},
        group.id,
      );
      setLocallyRemovedMemberIds((current) => {
        if (!userIds.some((userId) => current.has(userId))) return current;
        const next = new Set(current);
        userIds.forEach((userId) => next.delete(userId));
        return next;
      });
      await refresh();
      setAddOpen(false);
      activateNotification({
        title: 'Members added',
        content: `${userIds.length} ${
          userIds.length === 1 ? 'person was' : 'people were'
        } added to ${group.name}.`,
        kind: 'success',
      });
    } catch (error: any) {
      activateNotification({
        title: 'Members could not be added',
        content: parseErrorContent(error),
        kind: 'error',
      });
    } finally {
      setAdding(false);
    }
  };

  const removeMember = async () => {
    if (!group || !removingMember || removing || protectedGroup) return;
    const memberToRemove = removingMember;
    setRemoving(true);
    try {
      await request.removeGroupMember(
        {remove: {users: [memberToRemove.id]}},
        group.id,
      );
      setLocallyRemovedMemberIds((current) =>
        new Set(current).add(memberToRemove.id),
      );
      setRemovingMember(null);
      await refresh().catch(() => undefined);
      activateNotification({
        title: 'Member removed',
        content: `${displayName(memberToRemove)} was removed from ${
          group.name
        }.`,
        kind: 'success',
      });
    } catch (error: any) {
      activateNotification({
        title: 'Member could not be removed',
        content: parseErrorContent(error),
        kind: 'error',
      });
    } finally {
      setRemoving(false);
    }
  };

  if (!loading && !group) {
    return (
      <DetailPage>
        <Breadcrumbs>
          <button
            type="button"
            onClick={() => navigate('/workspace-settings/groups')}>
            Teams &amp; Groups
          </button>
          <span aria-hidden="true">›</span>
          <span>Group not found</span>
        </Breadcrumbs>
        <SummaryCard>
          <EmptyState>
            This group is no longer available. Return to Teams &amp; Groups to
            choose another group.
          </EmptyState>
        </SummaryCard>
      </DetailPage>
    );
  }

  return (
    <DetailPage>
      <Breadcrumbs aria-label="Breadcrumb">
        <button
          type="button"
          onClick={() => navigate('/workspace-settings/groups')}>
          Teams &amp; Groups
        </button>
        <span aria-hidden="true">›</span>
        <span>{group?.name || 'Group'}</span>
      </Breadcrumbs>

      {loading ? (
        <SummaryCard aria-label="Loading group">
          <ItemLoaderLine />
        </SummaryCard>
      ) : (
        <>
          <SummaryCard>
            <SummaryHeader>
              <SummaryHeading>
                <TitleLine>
                  <GroupTitle>{group.name}</GroupTitle>
                  <TypePill>{groupTypeName(group, groupType)}</TypePill>
                  {protectedGroup && (
                    <ReadOnlyPill>
                      <LockIcon /> Read only
                    </ReadOnlyPill>
                  )}
                </TitleLine>
                <MemberCount>
                  {members.length} {members.length === 1 ? 'member' : 'members'}
                </MemberCount>
              </SummaryHeading>
              {!protectedGroup && (
                <EditButton type="button" onClick={() => setEditOpen(true)}>
                  Edit group
                </EditButton>
              )}
            </SummaryHeader>
            <LeadSection>
              <LeadLabel>Team Lead:</LeadLabel>
              {lead ? (
                <LeadIdentity>
                  <Avatar
                    name={displayName(lead)}
                    userId={lead.id || leadId}
                    src={lead.avatar?.url || ''}
                    size="md"
                    tooltip={true}
                    timezone={lead.timezone}
                    customUsers={users}
                  />
                  <LeadCopy>
                    <LeadName>{displayName(lead)}</LeadName>
                    <LeadMeta>
                      Manager for {leadReportCount}{' '}
                      {leadReportCount === 1 ? 'member' : 'members'} in this
                      group
                    </LeadMeta>
                  </LeadCopy>
                </LeadIdentity>
              ) : (
                <LeadName>No lead assigned</LeadName>
              )}
            </LeadSection>
          </SummaryCard>

          <GroupMemberCard>
            <InlineMemberSearchBar>
              <InlineMemberSearchField>
                <MemberSearchInput
                  aria-label="Search group members"
                  placeholder="Search members"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
                <MemberSearchIcon aria-hidden="true">
                  <MagnifyingGlassIcon />
                </MemberSearchIcon>
              </InlineMemberSearchField>
              {!protectedGroup && (
                <MemberPrimaryButton
                  type="button"
                  onClick={() => setAddOpen(true)}>
                  Add member
                </MemberPrimaryButton>
              )}
            </InlineMemberSearchBar>
            <MemberTableHeader>
              <div>Name</div>
              <div>Timezone</div>
              <div>Group(s)</div>
              <div>Manager</div>
              <div style={{textAlign: 'center'}}>Action</div>
            </MemberTableHeader>

            {pagedMembers.length ? (
              pagedMembers.map((member) => {
                const manager =
                  (typeof member.reviewer === 'object' && member.reviewer) ||
                  users.find(
                    (candidate) => candidate.id === reviewerId(member.reviewer),
                  );
                const memberGroups = Array.isArray(member.group)
                  ? member.group
                  : [];
                const isManager = users.some(
                  (candidate) => reviewerId(candidate.reviewer) === member.id,
                );
                const isLead = member.id === leadId;

                return (
                  <MemberTableRow key={member.id}>
                    <MemberIdentity>
                      <Avatar
                        name={displayName(member)}
                        userId={member.id}
                        src={member.avatar?.url || ''}
                        size="md"
                        tooltip={true}
                        timezone={member.timezone}
                        customUsers={users}
                      />
                      <MemberIdentityText>
                        <MemberNameLine>
                          <MemberName title={displayName(member)}>
                            {displayName(member)}
                          </MemberName>
                          {isManager && (
                            <MemberRoleBadge>Manager</MemberRoleBadge>
                          )}
                          {!isManager && member.role === 'admin' && (
                            <MemberRoleBadge>Admin</MemberRoleBadge>
                          )}
                        </MemberNameLine>
                        <MemberMeta title={member.jobTitle || member.email}>
                          {member.jobTitle || member.email}
                        </MemberMeta>
                      </MemberIdentityText>
                    </MemberIdentity>
                    <MemberCell
                      title={formatTimezoneForDisplay(member.timezone)}>
                      {formatTimezoneForDisplay(member.timezone)}
                    </MemberCell>
                    <MemberGroupsCell>
                      {memberGroups.length ? (
                        <>
                          <GroupPill title={memberGroups[0].name}>
                            {memberGroups[0].name}
                          </GroupPill>
                          {memberGroups.length > 1 && (
                            <Tooltip
                              BodyTextNodeType={GroupMore}
                              text={`+${memberGroups.length - 1}`}
                              noCapitalize
                              bodyStyle={{cursor: 'help'}}
                              tooltipBody={memberGroups
                                .slice(1)
                                .map((candidate: any) => candidate.name)
                                .join(', ')}
                            />
                          )}
                        </>
                      ) : (
                        '—'
                      )}
                    </MemberGroupsCell>
                    <ManagerCell>
                      {manager ? (
                        <>
                          <Avatar
                            name={displayName(manager)}
                            userId={manager.id || reviewerId(member.reviewer)}
                            src={manager.avatar?.url || ''}
                            size="sm"
                            tooltip={false}
                          />
                          <span title={displayName(manager)}>
                            {displayName(manager)}
                          </span>
                        </>
                      ) : (
                        '—'
                      )}
                    </ManagerCell>
                    <MemberAction onClick={(event) => event.stopPropagation()}>
                      {!protectedGroup && (
                        <>
                          <MemberActionButton
                            type="button"
                            aria-label={`Actions for ${displayName(member)}`}
                            aria-expanded={menuMemberId === member.id}
                            onClick={() =>
                              setMenuMemberId(
                                menuMemberId === member.id ? '' : member.id,
                              )
                            }>
                            <HorizontalDots />
                          </MemberActionButton>
                          {menuMemberId === member.id && (
                            <MemberMenu style={{width: 190}}>
                              <MemberMenuButton
                                type="button"
                                $danger
                                disabled={isLead}
                                onClick={() => {
                                  setMenuMemberId('');
                                  setRemovingMember(member);
                                }}>
                                Remove from group
                              </MemberMenuButton>
                              {isLead && (
                                <MemberMenuHint>
                                  Change the team lead before removing this
                                  person.
                                </MemberMenuHint>
                              )}
                            </MemberMenu>
                          )}
                        </>
                      )}
                    </MemberAction>
                  </MemberTableRow>
                );
              })
            ) : (
              <MemberEmpty>
                {search
                  ? 'No members match your search.'
                  : 'This group has no members yet.'}
              </MemberEmpty>
            )}

            {visibleMembers.length > MEMBERS_PER_PAGE && (
              <MemberPagination aria-label="Group members pagination">
                <MemberPageButton
                  type="button"
                  disabled={page === 1}
                  onClick={() => setPage((current) => current - 1)}>
                  Previous
                </MemberPageButton>
                {Array.from({length: totalPages}, (_, index) => index + 1).map(
                  (pageNumber) => (
                    <MemberPageButton
                      type="button"
                      key={pageNumber}
                      $active={pageNumber === page}
                      aria-current={pageNumber === page ? 'page' : undefined}
                      onClick={() => setPage(pageNumber)}>
                      {pageNumber}
                    </MemberPageButton>
                  ),
                )}
                <MemberPageButton
                  type="button"
                  disabled={page === totalPages}
                  onClick={() => setPage((current) => current + 1)}>
                  Next
                </MemberPageButton>
              </MemberPagination>
            )}
          </GroupMemberCard>
        </>
      )}

      {group && editOpen && (
        <GroupEditSidecar
          group={group as TeamsAndGroupsGroup}
          groupTypes={groupType}
          users={users}
          onMutated={refresh}
          onClose={() => setEditOpen(false)}
        />
      )}

      {group && addOpen && (
        <AddMembersDialog
          groupName={group.name}
          candidates={candidates}
          loading={adding}
          onAdd={addMembers}
          onClose={() => setAddOpen(false)}
        />
      )}

      {group && removingMember && (
        <DialogOverlay role="presentation">
          <ModalShell role="dialog" aria-modal="true">
            <DialogHeader>
              <DialogTitle>Remove from {group.name}?</DialogTitle>
            </DialogHeader>
            <DialogBody>
              <ConfirmationText>
                {displayName(removingMember)} will lose access associated with
                this group. Their workspace account will remain active.
              </ConfirmationText>
              {removingMember.id === leadId && (
                <ErrorCopy>
                  Change the team lead before removing this person.
                </ErrorCopy>
              )}
            </DialogBody>
            <DialogFooter>
              <SecondaryButton
                type="button"
                disabled={removing}
                onClick={() => setRemovingMember(null)}>
                Cancel
              </SecondaryButton>
              <PrimaryButton
                type="button"
                disabled={removing || removingMember.id === leadId}
                onClick={removeMember}>
                {removing ? 'Removing…' : 'Remove from group'}
              </PrimaryButton>
            </DialogFooter>
          </ModalShell>
        </DialogOverlay>
      )}
    </DetailPage>
  );
});
